/**
 * Checkpoint GraphQL Queries (7 total)
 *
 * Complete query coverage for Checkpoint primitive:
 * 1. getCheckpoint - Single checkpoint by ID
 * 2. listCheckpointsByWorkflow - All checkpoints for workflow (sequence-ordered)
 * 3. getCheckpointsByTransaction - Saga transaction checkpoints (Formula #4)
 * 4. getLatestCheckpoint - Most recent checkpoint for workflow
 * 5. getCheckpointChain - Complete rollback dependency graph
 * 6. getRecoverableCheckpoints - Resume-enabled checkpoints
 * 7. getCheckpointStatistics - Aggregate metrics
 *
 * NOTE: tenantId is resolved from AuthCtx on the backend (not passed as parameter)
 */

import { gql } from '@apollo/client';
import { CHECKPOINT_FIELDS, CHECKPOINT_STATISTICS_FIELDS } from './fragments';

/**
 * List all checkpoints for tenant (paginated)
 */
export const LIST_CHECKPOINTS = gql`
  ${CHECKPOINT_FIELDS}
  query ListCheckpoints($limit: Int, $cursor: String) {
    listCheckpoints(limit: $limit, cursor: $cursor) {
      items {
        ...CheckpointFields
      }
      total
      nextCursor
      hasMore
    }
  }
`;

/**
 * Get checkpoint by ID
 */
export const GET_CHECKPOINT = gql`
  ${CHECKPOINT_FIELDS}
  query GetCheckpoint($checkpointId: String!) {
    getCheckpoint(checkpointId: $checkpointId) {
      ...CheckpointFields
    }
  }
`;

/**
 * List checkpoints by workflow instance (sequence-ordered)
 */
export const LIST_CHECKPOINTS_BY_WORKFLOW = gql`
  ${CHECKPOINT_FIELDS}
  query ListCheckpointsByWorkflow(
    $workflowInstanceId: String!
    $limit: Int
  ) {
    listCheckpointsByWorkflow(
      workflowInstanceId: $workflowInstanceId
      limit: $limit
    ) {
      ...CheckpointFields
    }
  }
`;

/**
 * Get checkpoints by saga transaction (Formula #4)
 */
export const GET_CHECKPOINTS_BY_TRANSACTION = gql`
  ${CHECKPOINT_FIELDS}
  query GetCheckpointsByTransaction($transactionId: String!) {
    getCheckpointsByTransaction(
      transactionId: $transactionId
    ) {
      ...CheckpointFields
    }
  }
`;

/**
 * Get latest checkpoint for workflow
 */
export const GET_LATEST_CHECKPOINT = gql`
  ${CHECKPOINT_FIELDS}
  query GetLatestCheckpoint($workflowInstanceId: String!) {
    getLatestCheckpoint(
      workflowInstanceId: $workflowInstanceId
    ) {
      ...CheckpointFields
    }
  }
`;

/**
 * Get checkpoint chain (rollback dependency graph)
 */
export const GET_CHECKPOINT_CHAIN = gql`
  ${CHECKPOINT_FIELDS}
  query GetCheckpointChain($checkpointId: String!) {
    getCheckpointChain(checkpointId: $checkpointId) {
      ...CheckpointFields
    }
  }
`;

/**
 * Get recoverable checkpoints (resume-enabled)
 */
export const GET_RECOVERABLE_CHECKPOINTS = gql`
  ${CHECKPOINT_FIELDS}
  query GetRecoverableCheckpoints($workflowInstanceId: String!) {
    getRecoverableCheckpoints(
      workflowInstanceId: $workflowInstanceId
    ) {
      ...CheckpointFields
    }
  }
`;

/**
 * Get checkpoint statistics for workflow
 */
export const GET_CHECKPOINT_STATISTICS = gql`
  ${CHECKPOINT_STATISTICS_FIELDS}
  query GetCheckpointStatistics($workflowInstanceId: String!) {
    getCheckpointStatistics(
      workflowInstanceId: $workflowInstanceId
    ) {
      ...CheckpointStatisticsFields
    }
  }
`;
